import React from 'react';
import { formatDateTime } from '@/utils/format';
import {
  formatFixAge,
  STALE_AFTER_MINUTES,
  summariseTracking,
} from '../rentTrackingDisplay';

export interface TrackingSummaryBarProps {
  summary: ReturnType<typeof summariseTracking>;
  /** The instant the API evaluated arrears and pin ages at. */
  asOf: string | null;
  className?: string;
}

const Stat: React.FC<{
  label: string;
  value: number;
  hint: string;
  tone?: string;
}> = ({ label, value, hint, tone = 'text-gray-900' }) => (
  <span
    className="inline-flex items-baseline gap-1 rounded-lg bg-gray-50 px-2 py-1"
    title={hint}
  >
    <b className={`text-[14px] ${tone}`}>{value}</b>
    <span className="text-[12px] text-gray-600">{label}</span>
  </span>
);

/**
 * What the numbers over the map mean, spelled out. Bare counts read as a
 * health score; ops needs to know that "0 fix segar" is usually stalled OEM
 * ingestion rather than units that vanished.
 */
export const TrackingSummaryBar: React.FC<TrackingSummaryBarProps> = ({
  summary,
  asOf,
  className = '',
}) => {
  const drawn = summary.total - summary.noFix;
  const freshest = summary.freshestFixMinutes;
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <Stat
          label="unit dipantau"
          value={summary.total}
          hint="Unit di rung yang dipilih, ditambah unit dengan case repossession terbuka."
        />
        <Stat
          label="punya case repo"
          value={summary.withCase}
          hint="Sudah ada case repossession terbuka — pekerjaan lapangan sudah jalan."
        />
        <Stat
          label={`fix < ${STALE_AFTER_MINUTES} menit`}
          value={summary.withFix}
          hint="Posisi terakhirnya masih baru, jadi titik di peta bisa dipercaya."
          tone="text-emerald-700"
        />
        <Stat
          label={`fix > ${STALE_AFTER_MINUTES} menit`}
          value={summary.stale}
          hint="Titiknya digambar, tapi unit bisa sudah pindah sejak fix terakhir."
          tone="text-amber-700"
        />
        <Stat
          label="belum pernah kirim fix"
          value={summary.noFix}
          hint="Tidak ada satu pun koordinat dari telemetry OEM, jadi unit ini tidak muncul di peta."
        />
      </div>
      <p className="text-[12px] text-gray-500">
        {drawn} dari {summary.total} unit tergambar di peta.{' '}
        {freshest === null
          ? 'Belum ada unit yang pernah mengirim koordinat.'
          : `Fix terbaru di daftar ini ${formatFixAge(freshest)}.`}
        {freshest !== null && freshest > STALE_AFTER_MINUTES
          ? ' Kalau seluruh unit ikut lama, biasanya pengiriman telemetry OEM yang tersendat, bukan unitnya yang hilang.'
          : ''}
        {asOf ? ` Data per ${formatDateTime(asOf)}.` : ''}
      </p>
    </div>
  );
};

export default TrackingSummaryBar;
